import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueService, LeagueSeasonService } from '../../services/firebaseService';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const schema = yup.object({
  survivalPoints: yup.number().required().min(0, 'Must be 0 or greater'),
  preMergePredictionPoints: yup.number().required().min(0, 'Must be 0 or greater'),
  postMergePredictionPoints: yup.number().required().min(0, 'Must be 0 or greater'),
  winnerPoints: yup.number().required().min(0, 'Must be 0 or greater'),
}).required();

const scoringRuleDescriptions = {
  survivalPoints: 'Points earned for each drafted contestant who survives a departure (weekly and finale)',
  preMergePredictionPoints: 'Points earned for correct predictions during pre-merge episodes',
  postMergePredictionPoints: 'Points earned for correct predictions during post-merge episodes',
  winnerPoints: 'Points earned for correctly predicting the ultimate winner in final predictions'
};

export default function ScoringRules() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      survivalPoints: 10,
      preMergePredictionPoints: 5,
      postMergePredictionPoints: 8,
      winnerPoints: 50,
    }
  });

  useEffect(() => {
    const fetchLeagueData = async () => {
      try {
        const leagueData = await LeagueService.getLeague(leagueId);
        setLeague(leagueData);

        const seasonData = await LeagueSeasonService.getLeagueSeason(leagueId, seasonId);
        setCurrentSeason(seasonData);

        if (seasonData?.scoringRules) {
          reset(seasonData.scoringRules);
        }
      } catch (error) {
        console.error('Error fetching league data:', error);
        setError('Failed to load league data');
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueData();
  }, [leagueId, seasonId, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Update the scoring rules (this method includes logging)
      await LeagueSeasonService.updateScoringRules(leagueId, seasonId, data, currentUser.uid);

      setSuccess('Scoring rules updated successfully!');
      
      // Refresh the season data
      const updatedSeason = await LeagueSeasonService.getLeagueSeason(leagueId, seasonId);
      setCurrentSeason(updatedSeason);
      
      // Reset form to mark as not dirty
      reset(data);
    } catch (error) {
      console.error('Error updating scoring rules:', error);
      setError('Failed to update scoring rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (currentSeason?.scoringRules) {
      reset(currentSeason.scoringRules);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!league || !currentSeason) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">League not found</h1>
            <p className="mt-2 text-gray-600">The league you're looking for doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(`/manage-leagues/${leagueId}`)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Scoring Rules</h1>
              <p className="mt-1 text-gray-600">
                Configure how points are awarded in {league.name}
                {currentSeason && (
                  <span className="ml-2 text-sm text-gray-500">
                    • Season: {currentSeason.seasonId}
                  </span>
                )}
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-green-800">{success}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Scoring Rules Configuration */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TrophyIcon className="w-6 h-6 text-survivor-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Scoring Configuration</h2>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={!isDirty}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={!isDirty || saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="max-w-2xl space-y-4">
                {/* Drafted Contestant Points */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Drafted Contestant Points</h3>
                  <div>
                    <label htmlFor="survivalPoints" className="block text-sm font-medium text-gray-700 mb-1">
                      Survival Points per Departure
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="survivalPoints"
                        {...register('survivalPoints')}
                        min="0"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                          errors.survivalPoints ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-gray-500 text-sm">pts</span>
                      </div>
                    </div>
                    {errors.survivalPoints && (
                      <p className="mt-1 text-sm text-red-600">{errors.survivalPoints.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-600">
                      {scoringRuleDescriptions.survivalPoints}
                    </p>
                  </div>
                </div>

                {/* Prediction Points */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Prediction Points</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="preMergePredictionPoints" className="block text-sm font-medium text-gray-700 mb-1">
                        Pre-Merge Prediction Points
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          id="preMergePredictionPoints"
                          {...register('preMergePredictionPoints')}
                          min="0"
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                            errors.preMergePredictionPoints ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <span className="text-gray-500 text-sm">pts</span>
                        </div>
                      </div>
                      {errors.preMergePredictionPoints && (
                        <p className="mt-1 text-sm text-red-600">{errors.preMergePredictionPoints.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-600">
                        {scoringRuleDescriptions.preMergePredictionPoints}
                      </p>
                    </div>
                    <div>
                      <label htmlFor="postMergePredictionPoints" className="block text-sm font-medium text-gray-700 mb-1">
                        Post-Merge Prediction Points
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          id="postMergePredictionPoints"
                          {...register('postMergePredictionPoints')}
                          min="0"
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                            errors.postMergePredictionPoints ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <span className="text-gray-500 text-sm">pts</span>
                        </div>
                      </div>
                      {errors.postMergePredictionPoints && (
                        <p className="mt-1 text-sm text-red-600">{errors.postMergePredictionPoints.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-600">
                        {scoringRuleDescriptions.postMergePredictionPoints}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Winner Points */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Winner Points</h3>
                  <div>
                    <label htmlFor="winnerPoints" className="block text-sm font-medium text-gray-700 mb-1">
                      Ultimate Winner Points
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="winnerPoints"
                        {...register('winnerPoints')}
                        min="0"
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                          errors.winnerPoints ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-gray-500 text-sm">pts</span>
                      </div>
                    </div>
                    {errors.winnerPoints && (
                      <p className="mt-1 text-sm text-red-600">{errors.winnerPoints.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-600">
                      {scoringRuleDescriptions.winnerPoints}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-blue-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">How Scoring Works</h3>
                <div className="mt-2 text-sm text-blue-700 space-y-1">
                  <p><strong>Survival Points:</strong> Teams earn points for each drafted contestant who survives a departure (weekly and finale).</p>
                  <p><strong>Pre-Merge Prediction Points:</strong> Points earned for correct predictions during pre-merge episodes.</p>
                  <p><strong>Post-Merge Prediction Points:</strong> Points earned for correct predictions during post-merge episodes.</p>
                  <p><strong>Winner Points:</strong> Teams earn bonus points for correctly predicting the ultimate winner.</p>
                  <p className="mt-2 text-xs text-blue-600">Changes to scoring rules will only affect future weeks. Points already awarded for completed weeks will not be recalculated.</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
